import dayjs from 'dayjs';
import { memo, useCallback, useState } from 'react';
import { Button, Image, Input, message } from 'antd';

/**
 * The same five llama faces the customer picks from in the review prompt, so a score reads the
 * same here as it did when it was given. Index 0 is score 1.
 */
const LLAMA_FACES = [
    { word: 'Bad', src: require('@/assets/images/llama/llama-1-bad.png') },
    { word: 'Poor', src: require('@/assets/images/llama/llama-2-poor.png') },
    { word: 'Okay', src: require('@/assets/images/llama/llama-3-okay.png') },
    { word: 'Good', src: require('@/assets/images/llama/llama-4-good.png') },
    { word: 'Excellent', src: require('@/assets/images/llama/llama-5-excellent.png') },
];

/** Scores are 1-5; anything outside that (or a missing score) has no face to show. */
const faceFor = (score: number) => LLAMA_FACES[Math.round(score) - 1];

type ReviewDraft = { ratingScore: number; ratingComment: string };
type ReviewListProps = {
    tableData: any[];
    /** When true, each review's stars + comment become editable with a per-review Save. */
    editable?: boolean;
    onSaveReview?: (review: { ratingId: string; ratingScore: number; ratingComment: string }) => Promise<void> | void;
};

const ReviewList = memo((props: ReviewListProps) => {
    const createTime = useCallback((time: string) => {
        return dayjs().isSame(dayjs(time), 'day')
            ? dayjs(time).format('hh:mm')
            : dayjs(time).format('hh:mm  YYYY/MM/DD');
    }, []);
    const [drafts, setDrafts] = useState<Record<string, ReviewDraft>>({});
    const [savingId, setSavingId] = useState('');
    const draftOf = (r: any): ReviewDraft =>
        drafts[r.ratingId] ?? { ratingScore: Number(r.ratingScore) || 0, ratingComment: r.ratingComment ?? '' };
    const setDraft = (r: any, patch: Partial<ReviewDraft>) =>
        setDrafts(prev => ({ ...prev, [r.ratingId]: { ...draftOf(r), ...patch } }));

    /**
     * A review now belongs to either a marketplace task or a B2B partner job, so the list says
     * which one it came from. `-` when the backend predates the reference columns.
     */
    const reviewSource = (r: any): string => {
        if (r.businessAdminJobId) {
            const label = r.jobRefId || r.jobProductName;
            return label ? `Job · ${label}` : 'Job';
        }
        if (r.taskId) {
            return r.taskTitle ? `Task · ${r.taskTitle}` : 'Task';
        }
        return '';
    };

    const save = async (r: any) => {
        const d = draftOf(r);
        if (!d.ratingScore || d.ratingScore < 1) {
            message.warning('Please select a star rating (1-5)');
            return;
        }
        setSavingId(String(r.ratingId));
        try {
            await props.onSaveReview?.({
                ratingId: String(r.ratingId),
                ratingScore: d.ratingScore,
                ratingComment: d.ratingComment,
            });
        } finally {
            setSavingId('');
        }
    };

    return (
        <>
            {props.tableData.map((r: any, i) => {
                const d = draftOf(r);
                const score = props.editable ? d.ratingScore : r.ratingScore;
                return (
                    <div className="review-item" key={`rating-item${i}`}>
                        <div className="commentator">
                            <div className="commentator-avatar">
                                <img src={r.avatarUrl ? r.avatarUrl : ''} alt="" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div className="commentator-name">{r.fullName ?? '-'}</div>
                                {props.editable ? (
                                    <div className="llama-score is-editable">
                                        {LLAMA_FACES.map((face, s) => (
                                            <button
                                                key={`llama-pick${i}-${s}`}
                                                type="button"
                                                className={`llama-score__face${score === s + 1 ? ' is-on' : ''}`}
                                                title={`${s + 1} of 5 — ${face.word}`}
                                                onClick={() => setDraft(r, { ratingScore: s + 1 })}
                                            >
                                                <img src={face.src} alt={face.word} />
                                                <span>{face.word}</span>
                                            </button>
                                        ))}
                                    </div>
                                ) : faceFor(score) ? (
                                    <div className="llama-score">
                                        <img src={faceFor(score).src} alt={faceFor(score).word} />
                                        <span className="llama-score__word">{faceFor(score).word}</span>
                                        <span className="llama-score__value">{score}/5</span>
                                    </div>
                                ) : (
                                    <div className="llama-score__value">-</div>
                                )}
                            </div>
                        </div>
                        {reviewSource(r) ? <div className="review-source">{reviewSource(r)}</div> : null}
                        {props.editable ? (
                            <>
                                <Input.TextArea
                                    rows={2}
                                    maxLength={255}
                                    value={d.ratingComment}
                                    placeholder="Review comment"
                                    onChange={e => setDraft(r, { ratingComment: e.target.value })}
                                    style={{ marginTop: 8 }}
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginTop: 8,
                                    }}
                                >
                                    <span className="create-time">{createTime(r.createTime)}</span>
                                    <Button
                                        type="primary"
                                        size="small"
                                        loading={savingId === String(r.ratingId)}
                                        onClick={() => save(r)}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="review-text">{r.ratingComment ?? '-'}</div>
                                {r.ratingImg?.filter(Boolean).length ? (
                                    <div className="review-photos">
                                        <Image.PreviewGroup>
                                            {r.ratingImg.filter(Boolean).map((img: string, imgIndex: number) => (
                                                <Image
                                                    key={`rating-photo${i}-${imgIndex}`}
                                                    src={img}
                                                    alt=""
                                                    width={72}
                                                    height={72}
                                                />
                                            ))}
                                        </Image.PreviewGroup>
                                    </div>
                                ) : null}
                                <div className="create-time">{createTime(r.createTime)}</div>
                            </>
                        )}
                    </div>
                );
            })}
        </>
    );
});
export default ReviewList;
